/**
 * Spatial Canvas with Living Floor
 * Combines the 2D spatial canvas with a 3D living floor background
 * Panels ARE the clouds when stamped into the floor
 */

import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as THREE from 'three';
import type { UserId } from '@maco144/shared';
import { LivingFloor, type LivingFloorInstance } from './living-floor';
import { SpatialCanvas } from './spatial-canvas';
import { useCanvas3DSync } from './use-canvas-3d-sync';
import type { PanelType } from './panel-layout-store';
import { WelcomeOverlay } from './welcome-overlay';
import { OnboardingTour, TOUR_DONE_KEY } from './onboarding-tour';
import { QuickCapture } from './quick-capture';

export interface SpatialCanvasWithFloorProps {
  userId: UserId;
  enableFloor?: boolean;
  floorParticleCount?: number;
  onPanelClose?: (panelType: PanelType) => void;
}

// TODO: Fix living floor performance - particle system has O(n*m) complexity
// Temporarily disabled (enableFloor default changed to false) due to:
// - 50K particles * panel count = 500K+ distance calculations/frame
// - Blocks main thread, freezes UI, prevents F12 from opening
// Fix: Use GPU-based particles or reduce panel collision checks

/**
 * Combined spatial canvas + living floor interface
 * Living floor runs as background, panels float on top with glassmorphism
 * Panels ARE the clouds when stamped into the floor
 */
export function SpatialCanvasWithFloor({
  userId,
  enableFloor = false,
  floorParticleCount = 5000, // Reduced from 20000 for better performance
  onPanelClose,
}: SpatialCanvasWithFloorProps) {
  const floorRef = useRef<LivingFloorInstance | null>(null);
  // NOTE: Do NOT call useCanvasState here — SpatialCanvas owns panel state.
  // A second useCanvasState instance creates a competing IDB store that
  // overwrites SpatialCanvas's saves, corrupting persisted layout.
  const emptyPanels = useRef(new Map<string, never>());
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem('concord-welcomed'));
  const [runTour, setRunTour] = useState(() => !localStorage.getItem(TOUR_DONE_KEY));

  const [containerDimensions, setContainerDimensions] = useState({ width: 1200, height: 800 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Cursor pos and 3D sync are only needed when the floor is enabled.
  // Tracking cursor state unconditionally triggers a full re-render on every
  // mousemove (60+ times/sec), freezing the UI even with the floor off.
  const [cursorPos, setCursorPos] = useState(new THREE.Vector3(0, 0, 0));
  const [activeUserIds] = useState<Set<string>>(new Set([userId]));
  const [zoomLevel, setZoomLevel] = useState(1);

  const particleConfig = useMemo(
    () => ({
      count: floorParticleCount,
      speed: 0.05,
      repulsion: 1.8,
      attraction: 0.03,
      friction: 0.92,
      boundsX: 100 * zoomLevel,
      boundsZ: 100 * zoomLevel,
      particleSize: 0.67,
    }),
    [floorParticleCount, zoomLevel]
  );

  // Only sync to the 3D floor when it's actually enabled. When disabled,
  // pass an empty map — the hook no-ops because floorRef.current is null.
  useCanvas3DSync(
    enableFloor
      ? { floorRef, panelStates: emptyPanels.current, cursorPos, activeUserIds }
      : { floorRef, panelStates: emptyPanels.current }
  );

  // Track container dimensions and (when floor enabled) cursor position
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      // Only update cursor state when the floor is active — every setState
      // here triggers a full re-render of SpatialCanvasWithFloor and all panels.
      if (enableFloor) {
        setCursorPos(new THREE.Vector3(e.clientX, e.clientY, 0));
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
    };
  }, [enableFloor]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        background: '#0a0e27',
      }}
    >
      {showWelcome && <WelcomeOverlay onDismiss={() => setShowWelcome(false)} />}
      {!showWelcome && (
        <OnboardingTour run={runTour} onFinish={() => setRunTour(false)} />
      )}
      <QuickCapture userId={userId} />
      {/* Living Floor Background - MUST NOT block interaction */}
      {enableFloor && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          <LivingFloor
            panelPositions={[]}
            cursorPos={cursorPos}
            activeUsers={Array.from(activeUserIds).map((_, idx) => ({
              x: Math.cos((idx / activeUserIds.size) * Math.PI * 2) * 20,
              y: 1,
              z: Math.sin((idx / activeUserIds.size) * Math.PI * 2) * 20,
              intensity: 1.0,
            }))}
            enabled={enableFloor}
            particleConfig={particleConfig}
            width={containerDimensions.width}
            height={containerDimensions.height}
            onReady={(floor) => {
              floorRef.current = floor;
            }}
          />
        </div>
      )}

      {/* Spatial Canvas - MUST be on top (z-index > 0) */}
      <div
        style={{
          position: 'absolute',
          zIndex: 1,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
        }}
      >
        <SpatialCanvas
          userId={userId}
          onPanelClose={onPanelClose}
          onZoomChange={(zoom) => setZoomLevel(zoom)}
        />
      </div>

    </div>
  );
}

export default SpatialCanvasWithFloor;
